/**
 * Landing query for the Alarms tab: every `:Alarm` in the graph paired
 * with its source `:Tag` and (when present) the parent UDTDefinition /
 * UDTInstance + upstream Compute that hosts the source tag.
 *
 * The Compute join lets the page kick off `/live-data/alarms/active`
 * against the right gateway(s) for live status. Per the PROVIDES
 * nodespec, a TagProvider can reach a leaf Tag four different ways:
 *
 *   1. top-level:       (tp)-[:PROVIDES]->(t)
 *   2. folder-nested:   (tp)-[:PROVIDES]->(Folder)-[:CONTAINS*]->(t)
 *   3. UDT-instance:    (tp)-[:PROVIDES]->(UDTInstance)-[:HAS_MEMBER*]->(t)
 *   4. UDT in folder:   (tp)-[:PROVIDES]->(Folder)-[:CONTAINS*]
 *                            ->(UDTInstance)-[:HAS_MEMBER*]->(t)
 *
 * We collapse all four into a single variable-length walk backwards
 * from the tag: zero-or-more CONTAINS/HAS_MEMBER hops up to the
 * PROVIDES anchor, then to the Compute. The `*0..*` leg covers case
 * 1 (zero hops, anchor = t); the others are walked through.
 * `head(collect(distinct gw))` collapses the (one-per-tag) result so
 * the alarm row isn't multiplied.
 *
 * Drained client-side via `drainCypherRows` because the unfiltered
 * cross-product of every alarm × instance × gateway easily outgrows a
 * single page on plants with dozens of UDT instances each carrying
 * several alarms. Priority filtering happens
 * server-side via the optional `$priorities` allow-list.
 */
export const PLANT_ALARMS_CYPHER = `
            MATCH (t:Tag)-[:HAS_ALARM]->(a:Alarm)
              WHERE ($priorities IS NULL OR a.priority IN $priorities)
            OPTIONAL MATCH (def:UDTDefinition)-[:HAS_ALARM_DEFINITION]->(a)
            OPTIONAL MATCH (inst:UDTInstance)-[:HAS_MEMBER]->(t)
            OPTIONAL MATCH
              (gw:Compute)-[:HAS_TAG_PROVIDER]->(tp:TagProvider)
                -[:PROVIDES]->(provided)
                  -[:CONTAINS|HAS_MEMBER*0..10]->(t)
            WITH a, t, def,
                 head(collect(distinct inst)) AS inst,
                 head(collect(distinct gw))   AS gateway
            RETURN a       AS alarm,
                   t       AS tag,
                   def     AS definition,
                   inst    AS instance,
                   gateway AS gateway
            ORDER BY toLower(coalesce(def.name, '~')),
                     toLower(coalesce(inst.name, '~')),
                     toLower(coalesce(a.tag_path, t.tag_path, t.name, '')),
                     toLower(coalesce(a.name, a.id))
            SKIP toInteger($skip) LIMIT toInteger($pageSize)
          `
